import AWS from 'aws-sdk';
import { ref, getDatabase, off, onValue } from 'firebase/database';
import { initializeApp } from 'firebase/app';
import { toast } from 'react-toastify';

import {
  dataFetcher,
  endpoints,
  AxiosCancelToken,
  baseUrl,
  firebaseCredentials,
  axios,
} from 'Api';
import {
  awsConfig,
  generateS3Url,
  getBucketName,
  getBucketNamePath,
} from '../Api/awsConfig';
// AWS Configuration
AWS.config.update({
  region: awsConfig.region,
  accessKeyId: awsConfig.accessKey,
  secretAccessKey: awsConfig.secretKey,
});
AWS.config.httpOptions.timeout = 0;
const s3 = new AWS.S3();
const s3Url = generateS3Url();
// Firebase setup
const firebaseApp = initializeApp(firebaseCredentials);
const firebaseDatabase = getDatabase(firebaseApp);
const databaseRef = ref(firebaseDatabase, 'status_from_sns');

// Global variables for upload tracking
const iteratorNum = [];
const totalNum = [];
let tempPercentages;

// AWS Upload Functions
async function initMultipartUpload(bucket, key) {
  const params = {
    Bucket: bucket,
    Key: key,
  };
  return s3.createMultipartUpload(params).promise();
}

const updateItem = (
  percentage,
  draft_id,
  filename,
  uploadPercentages,
  updateUploadPercentages
) => {
  const percent = parseInt((percentage / totalNum[draft_id]) * 100, 10);
  const trimmedFilename = filename.length > 26 ? `${filename.substring(0, 26)}...` : filename;
  const newPercentages = { ...uploadPercentages, [draft_id]: percent };
  tempPercentages = newPercentages;
  updateUploadPercentages(draft_id, percent);
  toast.update(draft_id, {
    render: `${trimmedFilename} ${percent}%`,
    autoClose: false,
  });
};

async function uploadPart(
  bucket,
  key,
  partNumber,
  partBody,
  uploadId,
  setVideoUploadedParts,
  filename,
  draft_id,
  uploadPercentages,
  updateUploadPercentages,
  partSize,
  startTime,
  fileSize,
  updateUploadDetails
) {
  const params = {
    Bucket: bucket,
    Key: key,
    PartNumber: partNumber,
    UploadId: uploadId,
    Body: partBody,
  };

  return s3
    .uploadPart(params)
    .promise()
    .then((data) => {
        const currentTime = new Date().getTime();
        const elapsedSize = (partNumber * partSize) / 1024 / 1024;
        const elapsedTime = (currentTime - startTime) / 1000;
        const remainingSize = (fileSize / 1024 / 1024) - elapsedSize;
        updateUploadDetails(draft_id, { elapsed_size: elapsedSize, elapsed_time: elapsedTime, remaining_size: remainingSize > 0 ? remainingSize : 0 });
      iteratorNum[draft_id] += 1;
      updateItem(
        iteratorNum[draft_id],
        draft_id,
        filename,
        uploadPercentages,
        updateUploadPercentages
      );
      return data;
    })
    .catch((error) => {
      throw error;
    });
}

function completeMultipartUpload(bucket, key, uploadId, parts) {
  const params = {
    Bucket: bucket,
    Key: key,
    UploadId: uploadId,
    MultipartUpload: {
      Parts: parts,
    },
  };
  return s3.completeMultipartUpload(params).promise();
}

const notify = (
  filename,
  draft_id,
  uploadPercentages,
  updateUploadPercentages
) => {
  setTimeout(() => {
    const newPercentages = {
      ...uploadPercentages,
      [draft_id]: 'Starting Upload',
    };
    tempPercentages = newPercentages;
    updateUploadPercentages(draft_id, 'Starting Upload');
    const trimmedFilename = filename.length > 26 ? `${filename.substring(0, 26)}...` : filename;
    toast(trimmedFilename, {
      autoClose: false,
      toastId: draft_id,
      position: toast.POSITION.BOTTOM_RIGHT,
      closeButton: false,
    });
  }, 1000);
};

const closeToast = (
  draft_id,
  message,
  type,
  setUploadContainer,
  setUploadContainer2
) => {
  toast.update(draft_id, {
    closeButton: false,
    render: message,
    type: type === 'success' ? toast.TYPE.SUCCESS : toast.TYPE.ERROR,
    autoClose: 3000,
  });
  if (setUploadContainer && setUploadContainer2) {
    setUploadContainer((prevUploadContainer) => prevUploadContainer.filter((id) => id !== draft_id));
    setUploadContainer2((prevUploadContainer) => prevUploadContainer.filter((id) => id !== draft_id));
  }
};

async function uploadPartSequentially(
  file,
  bucket,
  key,
  uploadId,
  partNumber,
  start,
  partSize,
  parts,
  draft_id,
  startTime,
  uploadPercentages,
  updateUploadPercentages,
  setUploadContainer,
  setUploadContainer2,
  updateUploadDetails
) {
  const end = Math.min(start + partSize, file.size);
  const data = {};
  const dataCallback = (snapshot) => {
    if (snapshot.exists()) {
      const dataObj = snapshot.val();
      data[draft_id] = dataObj[draft_id];
    }
  };
  onValue(databaseRef, dataCallback);
  const unsubscribe = () => {
    off(databaseRef, dataCallback);
  };
  if (data[draft_id]?.status !== 'DELETED') {
    await uploadPart(
      bucket,
      key,
      partNumber,
      file.slice(start, end),
      uploadId,
      () => {},
      file.name,
      draft_id,
      uploadPercentages,
      updateUploadPercentages,
      partSize,
      startTime,
      file.size,
      updateUploadDetails
    ).then((item) => {
      parts.push({ PartNumber: partNumber, ETag: item.ETag });
    });
  } else {
    setUploadContainer((prevUploadContainer) => prevUploadContainer.filter((id) => id !== draft_id));
    setUploadContainer2((prevUploadContainer) => prevUploadContainer.filter((id) => id !== draft_id));
  }
  unsubscribe();
  if (end < file.size) {
    await uploadPartSequentially(
      file,
      bucket,
      key,
      uploadId,
      partNumber + 1,
      end,
      partSize,
      parts,
      draft_id,
      startTime,
      uploadPercentages,
      updateUploadPercentages,
      setUploadContainer,
      setUploadContainer2,
      updateUploadDetails
    );
  }
}

// Helper function to create dummy object
const createDummy = (options) => {
  const {
    draft_id,
    title,
    fileName,
    thumbnail,
    ForNoImage,
    category,
    description,
    videoSize,
    selectedGroups,
    changeSize,
  } = options;

  // Clean up thumbnail URL - remove any extra commas or whitespace
  const cleanThumbnail = thumbnail
    ? thumbnail.replace(/,+$/, '').trim()
    : ForNoImage;

  return {
    draft_id,
    title: title || fileName,
    thumbnail: cleanThumbnail,
    category,
    video_description: description,
    video_size: changeSize(videoSize),
    type: 0,
    group_id: selectedGroups,
    content_status: 'Video',
    uploadIsTrying: 1,
  };
};

// Main upload function
export async function uploadLargeFile(options) {
  const {
    bucket,
    key,
    file,
    draft_id,
    uploadPercentages,
    updateUploadPercentages,
    setUploadContainer,
    setUploadContainer2,
    setIamUploading,
    onSuccess,
    onError,
    dummyData,
    updateDummy,
    setDraftCategoryState,
    updateUploadDetails
  } = options;

  // Create and update dummy object if dummyData and updateDummy are provided
  if (dummyData && updateDummy) {
    const dummy = createDummy({
      ...dummyData,
      draft_id,
    });
    updateDummy(dummy, 1);
  }

  setUploadContainer((prevUploadContainer) => [
    ...prevUploadContainer,
    draft_id,
  ]);
  setUploadContainer2((prevUploadContainer) => [
    ...prevUploadContainer,
    draft_id,
  ]);

  try {
    notify(file.name, draft_id, uploadPercentages, updateUploadPercentages);
    const { UploadId } = await initMultipartUpload(bucket, key);
    const minimumPartSize = 5 * 1024 * 1024;
    let partSize = file.size / 100;
    if (partSize < minimumPartSize) {
      partSize = minimumPartSize;
    }
    const fileSize = file.size;
    const partCount = Math.ceil(fileSize / partSize);
    const startTime = new Date().getTime();
    const parts = [];
    iteratorNum[draft_id] = 0;
    totalNum[draft_id] = partCount;
    await uploadPartSequentially(
      file,
      bucket,
      key,
      UploadId,
      1,
      0,
      partSize,
      parts,
      draft_id,
      startTime,
      uploadPercentages,
      updateUploadPercentages,
      setUploadContainer,
      setUploadContainer2,
      updateUploadDetails
    );

    const data = {};
    const dataCallback = (snapshot) => {
      if (snapshot.exists()) {
        const dataObj = snapshot.val();
        data[draft_id] = dataObj[draft_id];
      }
    };
    onValue(databaseRef, dataCallback);
    const unsubscribe = () => {
      off(databaseRef, dataCallback);
    };

    if (data[draft_id]?.status !== 'DELETED') {
      await completeMultipartUpload(bucket, key, UploadId, parts);
      closeToast(
        draft_id,
        'Uploading completed',
        'success',
        setUploadContainer,
        setUploadContainer2
      );
      const newPercentages = {
        ...uploadPercentages,
        [draft_id]: 'Please wait',
      };
      tempPercentages = newPercentages;
      updateUploadPercentages(draft_id, 'Please wait');
      iteratorNum[file.name] = 0;
      totalNum[draft_id] = 0;

      // Call success callback
      if (onSuccess) {
        onSuccess(draft_id);
      }

      unsubscribe();
    } else {
      updateUploadPercentages(draft_id, 'Cancelled');
      updateUploadDetails(draft_id, { elapsed_size: 0, elapsed_time: 0, remaining_size: 0 });
      closeToast(
        draft_id,
        'Upload has been canceled by user',
        'error',
        setUploadContainer,
        setUploadContainer2
      );
      unsubscribe();
    }
    unsubscribe();
  } catch (error) {
    updateUploadPercentages(draft_id, 'Cancelled');
    closeToast(
      draft_id,
      'Upload has been canceled by user',
      'error',
      setUploadContainer,
      setUploadContainer2
    );
    const newPercentages = { ...uploadPercentages, [draft_id]: 'Cancelled' };
    tempPercentages = newPercentages;
    updateUploadPercentages(draft_id, 'Cancelled');
    const allPropertiesAreNumericOrString = Object.values(
      uploadPercentages
    ).some(
      (value) => typeof value === 'number'
        || value === 'Starting Upload'
        || value === 'Initializing'
    );
    if (allPropertiesAreNumericOrString) {
      setIamUploading(false);
    }

    // Call error callback
    if (onError) {
      onError(error, draft_id);
    }
  }
}
